import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  Sparkles,
  Clock,
  Check,
  Search,
  FileText,
  Monitor,
  Rocket,
  Wrench,
  ChevronDown,
  Milestone,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardFooter,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { usePackageOfferings } from "@/providers/ContentProvider";
import type { PackageOfferingDto } from "@shared/types/PackageOfferingDto";

function getCategoryIcon(offering: PackageOfferingDto): React.ElementType {
  const id = offering.id;
  if (id === "pkg-concept-to-blueprint") return FileText;
  if (id === "pkg-mvp-rescue-audit") return Search;
  if (id === "pkg-desktop-feasibility") return Monitor;
  if (id === "pkg-ai-desktop-launch") return Rocket;
  if (id === "pkg-shipping-ops-partner") return Wrench;
  return Sparkles;
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatPriceRange(min: number, max: number): string {
  if (min === max) return formatCurrency(min);
  return `${formatCurrency(min)} to ${formatCurrency(max)}`;
}

interface PackageGridProps {
  onSelectPackage?: (offering: PackageOfferingDto) => void;
  layout?: "grid" | "horizontal";
}

export function PackageGrid({
  onSelectPackage,
  layout = "grid",
}: PackageGridProps) {
  const { offerings, isLoading } = usePackageOfferings();
  const navigate = useNavigate();
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const toggleExpanded = (id: string) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  const handleSelect = (offering: PackageOfferingDto, tierName?: string) => {
    if (onSelectPackage) {
      onSelectPackage(offering);
      return;
    }
    const params = new URLSearchParams({ packageId: offering.id });
    if (tierName) params.set("tier", tierName);
    navigate(`/intake?${params.toString()}`);
  };

  if (isLoading) {
    return layout === "horizontal" ? (
      <div className="flex flex-col gap-4 max-w-5xl mx-auto">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="h-28 rounded-xl border border-border/50 bg-card/50 animate-pulse"
          />
        ))}
      </div>
    ) : (
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="h-[520px] rounded-xl border border-border/50 bg-card/50 animate-pulse"
          />
        ))}
      </div>
    );
  }

  if (layout === "horizontal") {
    return (
      <div className="flex flex-col gap-4 max-w-5xl mx-auto">
        {offerings.map((offering, index) => (
          <HorizontalPackageCard
            key={offering.id}
            offering={offering}
            index={index}
            onSelect={handleSelect}
            expanded={expandedId === offering.id}
            onToggleExpand={() => toggleExpanded(offering.id)}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
      {offerings.map((offering, index) => (
        <PackageCard
          key={offering.id}
          offering={offering}
          index={index}
          onSelect={handleSelect}
          detailsOpen={expandedId === offering.id}
          onToggleDetails={() => toggleExpanded(offering.id)}
        />
      ))}
    </div>
  );
}

function PackageCard({
  offering,
  index,
  onSelect,
  detailsOpen,
  onToggleDetails,
}: {
  offering: PackageOfferingDto;
  index: number;
  onSelect: (offering: PackageOfferingDto) => void;
  detailsOpen: boolean;
  onToggleDetails: () => void;
}) {
  const Icon = getCategoryIcon(offering);
  const isPopular = offering.metadata.isPopular;
  const { pricing } = offering;
  const isRecurring = pricing.type === "RECURRING";

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1 }}
      className="relative"
    >
      {isPopular && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
          <Badge className="bg-gradient-to-r from-primary to-accent text-primary-foreground px-4 py-1 text-xs font-semibold shadow-lg shadow-primary/25">
            Most Popular
          </Badge>
        </div>
      )}
      <Card
        className={`h-full flex flex-col transition-all duration-300 hover:shadow-xl ${
          isPopular
            ? "border-primary/50 shadow-lg shadow-primary/10 scale-[1.02]"
            : "border-border/50 hover:border-primary/30"
        }`}
      >
        <CardHeader className="pb-4">
          <div className="flex items-center gap-3 mb-3">
            <div
              className={`w-11 h-11 rounded-lg flex items-center justify-center ${
                isPopular ? "bg-primary/20" : "bg-primary/10"
              }`}
            >
              <Icon className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h3 className="font-display text-lg font-semibold leading-tight">
                {offering.name}
              </h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                {offering.tagline}
              </p>
            </div>
          </div>
          <p className="text-sm text-muted-foreground leading-relaxed">
            {offering.description}
          </p>
        </CardHeader>

        <CardContent className="flex-1 space-y-5">
          {pricing.priceRange && (
            <div className="text-center py-3 rounded-lg bg-primary/5 border border-primary/10">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">
                {isRecurring ? "Starting at" : "Flat Fee"}
              </p>
              <div className="flex items-baseline justify-center gap-1">
                <span className="font-display text-2xl font-bold text-primary">
                  {formatPriceRange(
                    pricing.priceRange.min,
                    pricing.priceRange.max,
                  )}
                </span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {isRecurring ? "USD / month" : "USD one-time"}
              </p>
            </div>
          )}

          {isRecurring &&
            pricing.retainerTiers &&
            pricing.retainerTiers.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Monthly Tiers
                </p>
                <div className="space-y-1.5">
                  {pricing.retainerTiers.map((tier) => (
                    <div
                      key={tier.name}
                      className="flex items-center justify-between text-sm px-3 py-2 rounded-md bg-white/5 border border-white/5"
                    >
                      <div className="min-w-0">
                        <span className="text-foreground font-medium text-xs">
                          {tier.name}
                        </span>
                        <p className="text-[10px] text-muted-foreground">
                          {tier.responseTime} response
                        </p>
                      </div>
                      <span className="text-primary font-medium whitespace-nowrap ml-3 text-sm">
                        {formatCurrency(tier.monthlyPrice)}/mo
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Clock className="h-4 w-4" />
            <span>
              {isRecurring
                ? "Ongoing engagement"
                : `${offering.timelineEstimate} delivery`}
            </span>
          </div>

          <div className="flex flex-wrap gap-1.5">
            {offering.depositConfig.required &&
              offering.depositConfig.milestones.length > 0 && (
                <Badge variant="outline" className="text-[10px]">
                  {offering.depositConfig.milestones.length} milestones &middot;{" "}
                  {offering.depositConfig.depositPercentage}% deposit
                </Badge>
              )}
            {!offering.depositConfig.required &&
              !!offering.depositConfig.setupFee && (
                <Badge variant="outline" className="text-[10px]">
                  {formatCurrency(offering.depositConfig.setupFee)} setup fee
                </Badge>
              )}
            {offering.cloudSupport?.available && (
              <Badge variant="secondary" className="text-[10px]">
                Cloud support available
              </Badge>
            )}
          </div>

          <div>
            <AnimatePresence>
              {detailsOpen && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <div className="pt-3 space-y-4">
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        What&apos;s Included
                      </p>
                      <ul className="space-y-1.5">
                        {offering.features.map((feature) => (
                          <li key={feature} className="flex items-start gap-2">
                            <Check className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                            <span className="text-sm text-muted-foreground">
                              {feature}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    {offering.deliverables.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          Deliverables
                        </p>
                        <ul className="space-y-1.5">
                          {offering.deliverables.map((d) => (
                            <li key={d} className="flex items-start gap-2">
                              <Check className="h-4 w-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                              <span className="text-sm text-muted-foreground">
                                {d}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {offering.addons.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          Optional Add-ons
                        </p>
                        <ul className="space-y-1.5">
                          {offering.addons.map((addon) => (
                            <li
                              key={addon.id}
                              className="flex items-start justify-between gap-2"
                            >
                              <span className="text-sm text-muted-foreground">
                                {addon.name}
                              </span>
                              <span className="text-sm font-medium text-primary whitespace-nowrap">
                                +{formatCurrency(addon.price.amount)}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </CardContent>

        <CardFooter className="pt-4 flex-col gap-2">
          <Button
            onClick={() => onSelect(offering)}
            className={`w-full group ${
              isPopular
                ? "bg-gradient-to-r from-primary to-accent text-primary-foreground shadow-lg shadow-primary/25 hover:shadow-primary/40"
                : ""
            }`}
            size={isPopular ? "lg" : "default"}
            variant={isPopular ? "default" : "ghost"}
          >
            {offering.ctaText || "Get Started"}
            <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
          </Button>
          <button
            type="button"
            onClick={onToggleDetails}
            className="text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
          >
            {detailsOpen ? "Hide details" : "View details"}
          </button>
        </CardFooter>
      </Card>
    </motion.div>
  );
}

function HorizontalPackageCard({
  offering,
  index,
  onSelect,
  expanded,
  onToggleExpand,
}: {
  offering: PackageOfferingDto;
  index: number;
  onSelect: (offering: PackageOfferingDto, tierName?: string) => void;
  expanded: boolean;
  onToggleExpand: () => void;
}) {
  const [selectedTierIndex, setSelectedTierIndex] = useState(0);
  const Icon = getCategoryIcon(offering);
  const isPopular = offering.metadata.isPopular;
  const { pricing } = offering;
  const isRecurring = pricing.type === "RECURRING";
  const hasMoreFeatures = offering.features.length > 3;
  const hasDeliverables = offering.deliverables.length > 0;
  const hasAddons = offering.addons.length > 0;
  const hasMilestones =
    offering.depositConfig.required &&
    offering.depositConfig.milestones.length > 0;
  const hasTiers =
    isRecurring && !!pricing.retainerTiers && pricing.retainerTiers.length > 0;
  const selectedTier = hasTiers
    ? pricing.retainerTiers![selectedTierIndex]
    : null;
  const isExpandable =
    hasMoreFeatures || hasDeliverables || hasAddons || hasMilestones || hasTiers;

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.08 }}
      className="relative"
    >
      {isPopular && (
        <div className="absolute -top-2.5 left-6 z-10">
          <Badge className="bg-gradient-to-r from-primary to-accent text-primary-foreground px-3 py-0.5 text-[10px] font-semibold shadow-lg shadow-primary/25">
            Most Popular
          </Badge>
        </div>
      )}
      <Card
        className={`transition-all duration-300 hover:shadow-xl ${
          isPopular
            ? "border-primary/50 shadow-lg shadow-primary/10"
            : "border-border/50 hover:border-primary/30"
        }`}
      >
        <CardContent className="p-5 md:p-6">
          <div className="flex flex-col lg:flex-row gap-5">
            <div className="flex items-start gap-4 flex-1 min-w-0">
              <div
                className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${
                  isPopular ? "bg-primary/20" : "bg-primary/10"
                }`}
              >
                <Icon className="h-6 w-6 text-primary" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <h3 className="font-display text-lg font-semibold leading-tight">
                    {offering.name}
                  </h3>
                  <Badge variant="secondary" className="text-[10px]">
                    {isRecurring ? "Recurring" : offering.timelineEstimate}
                  </Badge>
                  {offering.depositConfig.required &&
                    offering.depositConfig.milestones.length > 0 && (
                      <Badge variant="outline" className="text-[10px]">
                        {offering.depositConfig.milestones.length} milestones
                        &middot; {offering.depositConfig.depositPercentage}%
                        deposit
                      </Badge>
                    )}
                  {!offering.depositConfig.required &&
                    !!offering.depositConfig.setupFee && (
                      <Badge variant="outline" className="text-[10px]">
                        {formatCurrency(offering.depositConfig.setupFee)} setup
                        fee
                      </Badge>
                    )}
                  {offering.cloudSupport?.available && (
                    <Badge variant="secondary" className="text-[10px]">
                      Cloud support available
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-muted-foreground mb-3">
                  {offering.description}
                </p>
                <div className="flex flex-wrap gap-x-4 gap-y-1.5">
                  {offering.features.slice(0, 3).map((feature) => (
                    <span
                      key={feature}
                      className="flex items-center gap-1.5 text-xs text-muted-foreground"
                    >
                      <Check className="h-3 w-3 text-primary shrink-0" />
                      {feature}
                    </span>
                  ))}
                  {isExpandable && (
                    <button
                      type="button"
                      onClick={onToggleExpand}
                      className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors font-medium"
                    >
                      {expanded
                        ? "Show less"
                        : hasTiers
                          ? `Compare ${pricing.retainerTiers!.length} tiers`
                          : `+${Math.max(offering.features.length - 3, 0) + offering.deliverables.length + offering.addons.length} more`}
                      <ChevronDown
                        className={`h-3 w-3 transition-transform duration-200 ${expanded ? "rotate-180" : ""}`}
                      />
                    </button>
                  )}
                </div>

                <AnimatePresence>
                  {expanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25 }}
                      className="overflow-hidden"
                    >
                      <div className="pt-4 space-y-4">
                        {hasMoreFeatures && (
                          <div className="space-y-2">
                            <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest">
                              All Features
                            </p>
                            <ul className="space-y-1.5">
                              {offering.features.map((feature) => (
                                <li
                                  key={feature}
                                  className="flex items-start gap-2"
                                >
                                  <Check className="h-3.5 w-3.5 text-primary mt-0.5 shrink-0" />
                                  <span className="text-xs text-muted-foreground">
                                    {feature}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {hasDeliverables && (
                          <div className="space-y-2">
                            <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest">
                              Deliverables
                            </p>
                            <ul className="space-y-1.5">
                              {offering.deliverables.map((d) => (
                                <li key={d} className="flex items-start gap-2">
                                  <Check className="h-3.5 w-3.5 text-emerald-400 mt-0.5 shrink-0" />
                                  <span className="text-xs text-muted-foreground">
                                    {d}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {hasAddons && (
                          <div className="space-y-2">
                            <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest">
                              Optional Add-ons
                            </p>
                            <ul className="space-y-1.5">
                              {offering.addons.map((addon) => (
                                <li
                                  key={addon.id}
                                  className="flex items-start justify-between gap-2"
                                >
                                  <span className="text-xs text-muted-foreground">
                                    {addon.name}
                                  </span>
                                  <span className="text-xs font-medium text-primary whitespace-nowrap">
                                    +{formatCurrency(addon.price.amount)}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {hasMilestones && (
                          <div className="space-y-2">
                            <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest">
                              Milestones
                            </p>
                            <div className="space-y-1.5">
                              {offering.depositConfig.milestones
                                .sort((a, b) => a.order - b.order)
                                .map((milestone) => (
                                  <div
                                    key={milestone.label}
                                    className="flex items-center gap-2 text-xs px-3 py-2 rounded-md bg-white/5 border border-white/5"
                                  >
                                    <Milestone className="h-3.5 w-3.5 text-primary shrink-0" />
                                    <span className="text-muted-foreground">
                                      {milestone.label}
                                    </span>
                                    {milestone.depositRequired && (
                                      <Badge
                                        variant="outline"
                                        className="text-[9px] ml-auto px-1.5 py-0"
                                      >
                                        Deposit
                                      </Badge>
                                    )}
                                  </div>
                                ))}
                            </div>
                          </div>
                        )}

                        {hasTiers && (
                          <div className="space-y-2">
                            <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest">
                              Support Tiers
                            </p>
                            <div className="space-y-2">
                              {pricing.retainerTiers!.map((tier, tierIdx) => {
                                const isSelected =
                                  tierIdx === selectedTierIndex;
                                return (
                                  <button
                                    key={tier.name}
                                    type="button"
                                    onClick={() =>
                                      setSelectedTierIndex(tierIdx)
                                    }
                                    className={`w-full text-left px-4 py-3 rounded-lg border transition-all ${
                                      isSelected
                                        ? "border-primary/50 bg-primary/5"
                                        : "border-white/5 bg-white/[0.02] hover:border-white/10 hover:bg-white/5"
                                    }`}
                                  >
                                    <div className="flex items-baseline justify-between mb-1">
                                      <span className="text-sm font-semibold text-foreground">
                                        {tier.name}
                                      </span>
                                      <span className="text-sm font-bold text-primary">
                                        {formatCurrency(tier.monthlyPrice)}/mo
                                      </span>
                                    </div>
                                    <p className="text-xs italic text-muted-foreground mb-2">
                                      {tier.focus}
                                    </p>
                                    <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                                      <span className="text-[10px] text-muted-foreground">
                                        <span className="text-foreground/70 font-medium">
                                          Response:
                                        </span>{" "}
                                        {tier.responseTime}
                                      </span>
                                      <span className="text-[10px] text-muted-foreground">
                                        <span className="text-foreground/70 font-medium">
                                          Hours:
                                        </span>{" "}
                                        Up to {tier.internalHourCap}h/mo
                                      </span>
                                      <span className="text-[10px] text-muted-foreground">
                                        <span className="text-foreground/70 font-medium">
                                          Maintenance:
                                        </span>{" "}
                                        {tier.maintenance}
                                      </span>
                                      <span className="text-[10px] text-muted-foreground">
                                        <span className="text-foreground/70 font-medium">
                                          AI Support:
                                        </span>{" "}
                                        {tier.aiSupport}
                                      </span>
                                    </div>
                                    {tier.strategy &&
                                      tier.strategy !== "None" && (
                                        <p className="text-[10px] text-muted-foreground mt-1">
                                          <span className="text-foreground/70 font-medium">
                                            Strategy:
                                          </span>{" "}
                                          {tier.strategy}
                                        </p>
                                      )}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row lg:flex-col items-start sm:items-center lg:items-end justify-between lg:justify-center gap-4 shrink-0 lg:pl-6 lg:border-l lg:border-border/30 lg:min-w-[200px]">
              {selectedTier ? (
                <div className="text-left lg:text-right">
                  <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider mb-0.5">
                    {selectedTier.name}
                  </p>
                  <span className="font-display text-xl font-bold text-primary">
                    {formatCurrency(selectedTier.monthlyPrice)}
                  </span>
                  <p className="text-[10px] text-muted-foreground">
                    USD / month
                  </p>
                </div>
              ) : pricing.priceRange ? (
                <div className="text-left lg:text-right">
                  <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider mb-0.5">
                    Flat Fee
                  </p>
                  <div className="flex items-baseline gap-1">
                    <span className="font-display text-xl font-bold text-primary">
                      {formatPriceRange(
                        pricing.priceRange.min,
                        pricing.priceRange.max,
                      )}
                    </span>
                  </div>
                  <p className="text-[10px] text-muted-foreground">
                    USD one-time
                  </p>
                </div>
              ) : null}

              <Button
                onClick={() => onSelect(offering, selectedTier?.name)}
                className={`group w-full md:w-auto ${
                  isPopular
                    ? "bg-gradient-to-r from-primary to-accent text-primary-foreground shadow-lg shadow-primary/25 hover:shadow-primary/40"
                    : ""
                }`}
                variant={isPopular ? "default" : "ghost"}
                size={isPopular ? "lg" : "default"}
              >
                {selectedTier
                  ? `Start with ${selectedTier.name}`
                  : offering.ctaText || "Get Started"}
                <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}
